class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer-inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer-inner') || this.querySelector('.drawer-close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    // Close the voucher modal too, so it doesn't still be open the next
    // time the drawer is reopened — the customer closing the whole drawer
    // is a clear enough signal they're done with it, voucher modal included.
    if (typeof window.setVoucherOpen === 'function') window.setVoucherOpen(false);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    // Wait rather than barge in — an in-flight quantity/remove/autoship
    // update elsewhere (cart-drawer.liquid) holds this same flag for its
    // full request+reconcile lifecycle. Racing that with this section
    // swap risks two things writing to the same DOM subtree at once.
    while (window.__cartDrawerRendering) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    window.__cartDrawerRendering = true;

    try {
      // The Liquid markup puts the is-empty class on <cart-drawer> itself
      // (this element), not on .drawer-inner — querying .drawer-inner for
      // it here was always false, so this never actually ran. Fixed to
      // check/clear it on the element that actually carries it.
      if (this.classList.contains('is-empty')) this.classList.remove('is-empty');

      this.productId = parsedState.id;

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      });

      if (typeof window.updateCartSectionVisibility === 'function') window.updateCartSectionVisibility();
      if (typeof window.updateShippingProgress === 'function') window.updateShippingProgress();

      await new Promise((resolve) => setTimeout(resolve));

      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();

      if (typeof bindAutoshipEvents === 'function') bindAutoshipEvents();
      if (typeof window.refreshCartDrawerUpsell === 'function') window.refreshCartDrawerUpsell();
      if (typeof refreshVoucherSystem === 'function') refreshVoucherSystem();
      if (typeof initLoopBundle === 'function') initLoopBundle('LOOP_heekrv-gq_bundles_drawer');
      if (window.reinitializeSmile) reinitializeSmile();
    } finally {
      window.__cartDrawerRendering = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

function defineCartDrawerItems() {
  if (typeof CartItems === 'undefined') {
    setTimeout(defineCartDrawerItems, 50);
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer-inner'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }
  }

  if (!customElements.get('cart-drawer-items')) {
    customElements.define('cart-drawer-items', CartDrawerItems);
  }
}

defineCartDrawerItems();

let smileInitTimeout = null;
let smileInitialized = false;

function reinitializeSmile() {
  if (smileInitialized) return;

  clearTimeout(smileInitTimeout);

  smileInitTimeout = setTimeout(() => {
    if (window.Smile && window.Smile.UI) {
      try {
        window.Smile.UI.reinitialize();
        smileInitialized = true;
        document.dispatchEvent(new Event('smile:reinitialize'));
      } catch (e) {
        console.warn('Smile reinit error', e);
      }
    }
  }, 500);
}

document.addEventListener('cart:updated', () => {
  if (window.refreshCartDrawerUpsell) {
    window.refreshCartDrawerUpsell();
  }
});

document.addEventListener('cart:updated', () => {

  if (window.refreshCartDrawerUI) {
    window.refreshCartDrawerUI();
  }

  smileInitialized = false;
  reinitializeSmile();
});

function openDrawerIfReturningFromCheckout() {
  let cameFromCheckout = false;
  try { cameFromCheckout = sessionStorage.getItem('wentToCheckout') === 'true'; } catch (err) {}
  if (!cameFromCheckout) return;

  try { sessionStorage.removeItem('wentToCheckout'); } catch (err) {}

  const drawer = document.querySelector('cart-drawer');
  if (!drawer || drawer.classList.contains('active')) return;

  // The cart may have changed while on checkout (inventory, a discount
  // applied there, etc.) — refresh before showing it, especially
  // important on a bfcache restore, where the drawer's JS state could
  // otherwise be a stale snapshot from before the customer left.
  document.dispatchEvent(new Event('cart:updated'));

  drawer.open();
}

// DOMContentLoaded fires much earlier than pageshow on a fresh (non-
// bfcache) reload — right after HTML/CSS/sync scripts parse, without
// waiting for images or third-party scripts to finish loading, which is
// what made the drawer feel slow to appear on a plain reload. pageshow
// stays as the fallback specifically for bfcache restores, where
// DOMContentLoaded never fires at all (the DOM isn't re-parsed).
document.addEventListener('DOMContentLoaded', openDrawerIfReturningFromCheckout);
window.addEventListener('pageshow', openDrawerIfReturningFromCheckout);